
<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from "@/plugins/axios";
import { useLayoutComponents } from '@/composables/layout.js';
const { Toast } = useLayoutComponents();

import { useToast } from '@/composables/use-toast';
const { addToast } = useToast();

import UsersHeader from '@/pages/admin/AdminHeader.vue';
import TextInput from '@/components/forms/inputs/text-input.vue';
import SelectInput from '@/components/forms/inputs/select-input.vue';

import SchemaTable from '@/pages/explore/SchemaTable.vue';
import RFPConfirmation from '@/pages/RFPConfirmation.vue';

const showSchemaModal = ref(false);
const showAcceptConfirmation = ref(false)
const acceptedRFP = ref(null); 

const rfps = ref([]);
const expandedRows = ref([]);
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const isAcceptLoading = ref(false);
const isRejectLoading = ref(false);

const getRFPs = async () => {
    try {
        const response = await axios.get('/api/rfp-requests/to-approve');
        if (response.data && Array.isArray(response.data)) {
            rfps.value = response.data.sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });

        } else {
            console.error('Invalid response format:', response.data);
            rfps.value = [];
        }
    } catch (error) {
        console.error('Error fetching RFPs:', error);
        addToast('Error fetching RFPs. Please try again.', 'error');
        rfps.value = [];
    }
};



const getStatus = (status) => {
    const statusConfig = {
        'pending_admin': {
            color: 'amber',
            classes: 'bg-amber-100 text-amber-800 dark:bg-amber-600 dark:text-black font-semibold'
        },
        'pending': {
            color: 'amber',
            classes: 'bg-amber-100 text-amber-800 dark:bg-amber-600 dark:text-black font-semibold'
        },
        'approved': {
            color: 'emerald',
            classes: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-600 dark:text-black font-semibold'
        },
        'rejected': {
            color: 'red',
            classes: 'bg-red-100 text-red-800 dark:bg-red-600 dark:text-black font-semibold'
        }
    }
    return {
        color: statusConfig[status]?.color || 'gray',
        classes: statusConfig[status]?.classes || 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-black font-semibold',
        text: status ? status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ') : 'Unknown'
    }
}

const formatStatusText = (status) => {
    if (!status) return '';
    
    switch (status.toLowerCase()) {
        case 'accepted':
            return 'Accepted';
        case 'rejected':
            return 'Rejected';
        case 'pending_admin':
            return 'Reviewing';
        case 'refused_admin':
            return 'Rejected';
            case 'pending_applicant':
                return 'Pending';
        default:
            return status.charAt(0).toUpperCase() + status.slice(1);
    }
}

const filteredRFPs = computed(() => {
    let filtered = rfps.value;
    
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(rfp => 
            rfp.listing?.applicant?.name?.toLowerCase().includes(query) ||
            rfp.listing?.applicant?.email?.toLowerCase().includes(query) ||
            rfp.listing?.name?.toLowerCase().includes(query) ||
            rfp.dataset?.name?.toLowerCase().includes(query)
        );
    }
    
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredRFPs.value.length / itemsPerPage.value);
});

const paginatedRFPs = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredRFPs.value.slice(start, end);
});

const toggleDetails = (id) => {
    const index = expandedRows.value.indexOf(id);
    if (index === -1) {
        expandedRows.value.push(id);
    } else {
        expandedRows.value.splice(index, 1);
    }
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const acceptRFP = async (rfpId) => {
    isAcceptLoading.value = true;
    try {
        const endpoint = "approve"; 
        const response = await axios.post(`/api/rfp-requests/${endpoint}/${rfpId}`)
        if(response.status === 200 || response.status === 201) {
            
            const message = 'RFP approved';
            const type = 'success';
            addToast(message, type);
            await getRFPs();
        }
    } catch (error) {
        console.error('Failed to process RFP request:', error);
        addToast('Erro ao processar solicitação. Tente novamente.', 'error');
    } finally {
        isAcceptLoading.value = false;
    }
}

const rejectRFP = async (rfpId) => {
    isRejectLoading.value = true;
   
    try {
        const response = await axios.post(`/api/rfp-requests/reject/${rfpId}`)
        if(response.status === 200 || response.status === 201) {
            
            const message = 'RFP rejected';
            const type = 'success';
            addToast(message, type);
            
            await getRFPs();
        }
    } catch (error) {
        console.error('Failed to reject RFP request:', error);
        addToast('Erro ao rejeitar solicitação. Tente novamente.', 'error');
    } finally {
        isRejectLoading.value = false;
    }
}

onMounted(() => {
    getRFPs();
});
</script>
<template>
    <RFPConfirmation 
          v-if="showAcceptConfirmation" 
          :show-navbar="false"
          :title="'RFP Accepted Successfully!'"
          :message="'You will be redirected in'"
          :button-text="'Redirect to RFP'"
          :redirect-path="`/item-detail/${acceptedRFP?.listing?.id}`"
          :show-countdown="true"
          :countdown-duration="5"
      />
  <Toast />
    <section class="p-5 bg-echoes-background-black">
            <UsersHeader>
            <template #title>RFP Arbitration</template>
            <template #description>
                Manage RFP requests for user accounts.
            </template>
        </UsersHeader>









        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search by listing name, dataset name, or seller info" 
                                class="w-full"
                            />
                        </div>
                        <div class="w-full sm:w-24">
                            <SelectInput
                                v-model="itemsPerPage"
                                :options="itemsPerPageOptions"
                                :hide-search="true"
                                @update:modelValue="currentPage = 1"
                            />
                        </div>
                    </div>
                </div>

                
                <!-- Table -->
                <div>
                    <div v-if="rfps.length === 0" class="h-[50vh]">
                        <div class="flex items-center justify-center h-full">
                            <div class="text-white/80 text-sm">No RFPs found</div>
                        </div>
                    </div>
                    <table v-else class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Listing
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Dataset
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Owner
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Request Date
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-for="rfp in paginatedRFPs" :key="rfp.id">
                                   <!-- Schema Modal -->
                                    <div v-if="showSchemaModal" class="fixed inset-0 bg-echoes-background-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                                       <div class="bg-echoes-background-gray rounded-[21px] w-[90%] max-w-4xl max-h-[80vh] relative shadow-xl border border-echoes-border-light overflow-hidden">
                                         <div class="px-4 py-3 border-b border-echoes-border-light flex items-center justify-between">
                                           <h2 class="text-base font-semibold bg-gradient-to-r from-echoes-primary to-echoes-primary/80 bg-clip-text text-transparent">Schema Details</h2>
                                            <button @click="showSchemaModal = false" class="inline-flex items-center justify-center w-7 h-7 rounded text-white/80 hover:text-echoes-primary hover:bg-echoes-background-black/50 transition-colors">
                                                <i class="mdi mdi-close text-lg"></i>
                                            </button>
                                        </div>
                                        <div class="p-4 overflow-y-auto custom-scrollbar" style="max-height: calc(80vh - 3.5rem);">
                                            <SchemaTable :schema="rfp.dataset.schema" />
                                        </div>
                                       </div>
                                    </div>
            
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-300">
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-14 w-14">
                                                <img v-if="rfp.listing?.preview_image" 
                                                     :src="rfp.listing.preview_image" 
                                                     :alt="rfp.listing.name"
                                                     class="h-14 w-14 rounded-lg object-cover">
                                                <div v-else class="h-14 w-14 rounded-lg bg-echoes-border-light flex items-center justify-center">
                                                    <i class="uil uil-image text-white/60 text-xl"></i>
                                                </div>
                                            </div>
                                            <div class="ml-4">
                                                <div class="text-sm font-medium text-white truncate max-w-[150px]" :title="rfp.listing?.name">
                                                    {{ rfp.listing?.name || 'N/A' }}
                                                </div>
                                                <div class="text-sm text-white/80 truncate max-w-[100px]" :title="rfp.listing?.short_description">
                                                    {{ rfp.listing?.short_description || 'N/A' }}
                                                </div>
                                                <div class="text-sm text-white/60">
                                                    ${{ rfp.listing?.price }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <div class="h-10 w-10 rounded-lg bg-echoes-border-light flex items-center justify-center">
                                                    <i class="uil uil-database text-white/60 text-lg"></i>
                                                </div>
                                            </div>
                                            <div class="ml-3">
                                                <div class="text-sm font-medium text-white truncate max-w-[130px]" :title="rfp.dataset?.name">
                                                    {{ rfp.dataset?.name || 'N/A' }}
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ rfp.dataset?.file_extension?.toUpperCase() || 'N/A' }} • {{ formatFileSize(rfp.dataset?.file_size) }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <div class="h-10 w-10 rounded-full bg-echoes-primary flex items-center justify-center">
                                                    <span class="text-lg font-medium text-white">
                                                        {{ rfp.dataset.seller?.user?.username?.charAt(0).toUpperCase() }}
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="ml-3">
                                                <div class="text-sm font-medium text-white truncate max-w-[120px]" :title="rfp.dataset.seller?.user?.username">
                                                    {{ rfp.dataset.seller?.user?.username || 'N/A' }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <span class="px-2 py-1.5 text-xs font-medium rounded-full" :class="getStatus(rfp.status).classes">
                                            {{ formatStatusText(rfp.status) }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-medium text-white">
                                            {{ formatDate(rfp.created_at) || 'No Request Date' }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                        <div class="flex justify-center space-x-3">
                                            <button @click="toggleDetails(rfp.id)"
                                                class="inline-flex items-center w-fit px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-300 border border-echoes-border-light">
                                                <svg 
                                                    class="w-5 h-5 mr-2 text-white/80 transition-transform duration-300"
                                                    :class="{ 'transform rotate-180': expandedRows.includes(rfp.id) }"
                                                    xmlns="http://www.w3.org/2000/svg" 
                                                    viewBox="0 0 20 20" 
                                                    fill="currentColor"
                                                >
                                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                                </svg>
                                                {{ expandedRows.includes(rfp.id) ? 'Details' : 'Details' }}
                                            </button>
                                            <div v-if="rfp.status === 'pending_admin'" class="flex gap-2">
                                                <button 
                                                    @click="rejectRFP(rfp.id)" 
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors duration-300 border border-red-600/30"
                                                >
                                                    <span class="flex items-center gap-2">
                                                        <i class="uil uil-times"></i>
                                                        <span>Reject</span>
                                                    </span>
                                                </button>
                                                <button 
                                                        @click="acceptRFP(rfp.id)" 
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors duration-300 border border-green-600/30"
                                                >
                                                    <span class="flex items-center gap-2">
                                                        <i class="uil uil-check"></i>
                                                        <span>Approve</span>
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="expandedRows.includes(rfp.id)" class="bg-echoes-background-black border-b w-full border-echoes-border-light">
                                    <td colspan="6" class="w-full px-6 py-4">
                                        <div class="w-full">
                                            <div class="flex w-full gap-4">
                                                <!-- Listing Details Section -->
                                                <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-4 w-full">
                                                    <h4 class="text-sm font-semibold text-white border-b border-echoes-border-light pb-2 mb-4 flex items-center">
                                                        <i class="uil uil-store text-echoes-primary mr-2"></i>
                                                        Listing Information
                                                    </h4>
                                                    <div class="grid grid-cols-1 gap-4">
                                                        <div class="space-y-2">
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Name:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.listing?.name || 'N/A' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Price:</div>
                                                                <div class="text-sm font-semibold text-white">${{ rfp.listing?.price || '0.00' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Description:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.listing?.description || 'N/A' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Short Description:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.listing?.short_description || 'N/A' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Applicant:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.listing?.applicant?.username || 'N/A' }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <!-- Dataset Details Section -->
                                                <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-4 w-full">
                                                    <h4 class="text-sm font-semibold text-white border-b border-echoes-border-light pb-2 mb-4 flex items-center">
                                                        <i class="uil uil-database text-echoes-primary mr-2"></i>
                                                        Dataset Information
                                                    </h4>
                                                    <div class="grid grid-cols-1 gap-4">
                                                        <div class="space-y-2">
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Name:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.dataset?.name || 'N/A' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">File:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.dataset?.file_name || 'N/A' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Owner:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.dataset?.seller?.user?.username || 'N/A' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Size:</div>
                                                                <div class="text-sm font-semibold text-white">{{ formatFileSize(rfp.dataset?.file_size) || 'N/A' }}</div>
                                                            </div>
                                                            <div class="flex gap-2">
                                                                <div class="text-sm text-white/80">Type:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.dataset?.file_extension?.toUpperCase() || 'N/A' }}</div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <button 
                            @click="showSchemaModal = true"
                            class="inline-flex items-center justify-center w-full gap-2 px-3.5 py-1.5 text-sm font-medium bg-echoes-primary hover:bg-echoes-primary/50 rounded transition-colors flex-shrink-0">
                            <i class="mdi mdi-table text-sm"></i>
                            View Schema
                        </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <!-- Seller Details Section -->
                                                <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-4 w-full">
                                                    <h4 class="text-sm font-semibold text-white border-b border-echoes-border-light pb-2 mb-4 flex items-center">
                                                        <i class="uil uil-user text-echoes-primary mr-2"></i>
                                                        Seller Information
                                                    </h4>
                                                    <div class="grid grid-cols-1 gap-4">
                                                        <div class="space-y-2">
                                                            <div v-if="rfp.dataset.seller?.user?.name" class="flex gap-2">
                                                                <div class="text-sm text-white/80">Name:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.dataset.seller?.user?.name }}</div>
                                                            </div>
                                                            <div v-if="rfp.dataset.seller?.user?.email" class="flex gap-2">
                                                                <div class="text-sm text-white/80">Email:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.dataset.seller?.user?.email || 'N/A' }}</div>
                                                            </div>
                                                            <div v-if="rfp.dataset.seller?.user?.username" class="flex gap-2">
                                                                <div class="text-sm text-white/80">Username:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.dataset.seller?.user?.username || 'N/A' }}</div>
                                                            </div>
                                                            <div v-if="rfp.seller_message" class="flex gap-2">
                                                                <div class="text-sm text-white/80">Message:</div>
                                                                <div class="text-sm font-semibold text-white">{{ rfp.seller_message || 'N/A' }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredRFPs.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredRFPs.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="page in totalPages" :key="page">
                                        <button
                                            @click="currentPage = page"
                                            :class="[
                                                'px-3 py-1 rounded-lg text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
     
</template>



<style scoped lang="postcss">
.rfp-arbitration__hero {
    @apply relative pt-16 pb-6 mb-6 border-b border-gray-200 dark:border-white/10 bg-gray-100;
    background-size: 100% 100%;
    background-position: 0px 0px;
    background-repeat: no-repeat;
}

.dark .rfp-arbitration__hero {
    background: linear-gradient(to bottom, #1e293b, #0f172a, #020617);
}

.rfp-arbitration__container {
    @apply relative overflow-hidden w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-20;
}

.btn {
    @apply inline-flex items-center justify-center font-medium transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed;
}
</style>